import {Evaluator} from "@cm/graph-v3"
import {registerImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/register-image-op-node"
import {AnyImageOpContext, ImageOpOutputs} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/assemble-result/definition"
import {Box2Like, Vector2Like} from "@cm/math"
import {assertNever} from "@cm/utils"
import {ImageOpNodeBlit} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/blit/node"
import {ImageOpNodeExtractRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/image-op-nodes/primitive/extract-region/node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {CompositeImageOpNode} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/composite-image-op-node"
import {Definition} from "@app/textures/texture-editor/operator-stack/operators/test/node/definition"

const nodeDefinition = {...definition, nodeClass: "ImageOpNodeTilingAssembleResult"}

@registerImageOpNode
export class ImageOpNodeTilingAssembleResult extends CompositeImageOpNode<typeof definition> {
    override get definition() {
        return nodeDefinition
    }

    override async execute(evaluator: Evaluator<AnyImageOpContext>, imageOpNodeBag: ImageOpNodeBag): Promise<ImageOpOutputs<typeof Definition>> {
        const {mappedImage, blendedBorder, borderDirection} = await evaluator.getAll(this.parameters)
        let cropRegion: Box2Like
        let lowBorderOffset: Vector2Like
        let highBorderOffset: Vector2Like
        switch (borderDirection) {
            case "horizontal":
                cropRegion = {
                    x: 0,
                    y: blendedBorder.descriptor.height / 2,
                    width: mappedImage.descriptor.width,
                    height: mappedImage.descriptor.height - blendedBorder.descriptor.height,
                }
                lowBorderOffset = {
                    x: 0,
                    y: -blendedBorder.descriptor.height / 2,
                }
                highBorderOffset = {
                    x: 0,
                    y: cropRegion.height - blendedBorder.descriptor.height / 2,
                }
                break
            case "vertical":
                cropRegion = {
                    x: blendedBorder.descriptor.width / 2,
                    y: 0,
                    width: mappedImage.descriptor.width - blendedBorder.descriptor.width,
                    height: mappedImage.descriptor.height,
                }
                lowBorderOffset = {
                    x: -blendedBorder.descriptor.width / 2,
                    y: 0,
                }
                highBorderOffset = {
                    x: cropRegion.width - blendedBorder.descriptor.width / 2,
                    y: 0,
                }
                break
            default:
                assertNever(borderDirection)
        }
        const croppedMappedImage = imageOpNodeBag.getNode("cropMappedImage", ImageOpNodeExtractRegion, {
            sourceImage: mappedImage,
            region: cropRegion,
        })
        const croppedMappedImageWithLowBorder = imageOpNodeBag.getNode("blitLowBorder", ImageOpNodeBlit, {
            backgroundImage: croppedMappedImage,
            foregroundImage: blendedBorder,
            position: lowBorderOffset,
        })
        const result = imageOpNodeBag.getNode("blitHighBorder", ImageOpNodeBlit, {
            backgroundImage: croppedMappedImageWithLowBorder,
            foregroundImage: blendedBorder,
            position: highBorderOffset,
        })
        return await evaluator.get(result)
    }
}
